import React, { useEffect, useRef, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import {
  CreditCard,
  Smartphone,
  ArrowLeft,
  Lock,
  User,
  MapPin,
  Clock,
  DollarSign,
  Plus,
  Minus,
  Trash2,
  Wallet,
  ArrowRight,
  CheckCircle,
  Package,
  Star,
  Store,
  Truck,
  Phone,
  StickyNote,
  Info,
} from "lucide-react";
import { Footer } from "../components/Footer";
import { useApp } from "../App";
import {
  CardElement,
  useStripe,
  useElements,
  PaymentRequestButtonElement,
} from "@stripe/react-stripe-js";
import {
  CartItem,
  CartItemDto,
  CreateClientSecret,
  Customization,
  OptionGroup,
  OrderQuotesDto,
  PlaceOrderDto,
  TimeSlot,
} from "@/types";
import {
  createPaymentIntent,
  getAllMenuItems,
  placeOrder,
  requestQuotes,
} from "@/services/menuService";
import {
  formatCanadianPhoneNumber,
  getMenuItemImageUrl,
} from "@/services/utlits";
import { DeliveryTypeModal } from "@/components/DeliveryTypeModal";
import { CartNotification } from "@/components/CartNotification";
import { LoadScript, Autocomplete, Libraries } from "@react-google-maps/api";
import apiConfig from "@/configs/apiConfig";
import { Textarea } from "@/components/ui/textarea";
type CheckoutStep = "delivery" | "pickup" | "payment" | "review";
// const libraries: Libraries[] = ["places"];
// const libraries = ['places'];

export function CheckoutPage() {
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAP_API_KEY;
  const {
    user,
    restaurant,
    cartItems,
    updateCartQuantity,
    removeFromCart,
    clearCart,
    orderMode,
    setOrderMode,
    addToCart,
  } = useApp();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState<CheckoutStep>("delivery");
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [isProcessing, setIsProcessing] = useState(false);
  const [tipPercentage, setTipPercentage] = useState([18]);
  const [customTip, setCustomTip] = useState("");
  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);

  const [menuItems, setMenuItems] = useState([]);

  const [showCartToast, setShowCartToast] = useState(false);
  const [cartItemName, setCartItemName] = useState("");
  const [cartItemQty, setCartItemQty] = useState(1);

  // Card form state
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [cardName, setCardName] = useState("");
  const [tipAmount, setTipAmount] = useState<number>(0);
  // Address state
  const [deliveryAddress, setDeliveryAddress] = useState(null);
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [phone, setPhone] = useState("");
  const [instructions, setInstructions] = useState(""); // delivert instructions
  const [specialInstructions, setSpecialInstructions] = useState(""); // Order instructions

  // Strip card details
  const stripe = useStripe();
  const elements = useElements();
  const [cardComplete, setCardComplete] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [tax, setTax] = useState(0);
  const [serviceFee, setServiceFee] = useState(0);
  const [orderTaxes, setOrderTaxes] = useState([]);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [quoteId, setQuoteId] = useState("");
  const [isDeliveryUnavailable, setIsDeliveryServiceUnavailable] =
    useState(false);
  const [openTaxToolTip, setOpenTaxToolTip] = useState(false);

  const subtotal = cartItems.reduce(
    (sum, item) => sum + (item.totalPrice || item.basePrice) * item.quantity,
    0,
  );
  // const deliveryFee =
  //   orderMode?.type === "pickup"
  //     ? 0
  //     : subtotal > 35
  //     ? 0
  //     : apiConfig.DELIVERY_FEE;

  useEffect(() => {
    // Load user from localStorage
    const storedUser = localStorage.getItem("auth_user");
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser); // make sure parse is JSON.parse
        console.log("1", parsedUser);
        if (parsedUser && parsedUser.savedAddresses) {
          console.log("2", parsedUser);
          let savedAddresses = parsedUser.savedAddresses;
          setCardName(savedAddresses.fullName || user?.name);
          setPhone(savedAddresses.phoneNumber || "");
          if (orderMode.type == "delivery") {
            console.log("3", parsedUser);
            setAddress(savedAddresses.addressLine1 || "");
            setCity(savedAddresses.city || "");
            setState(savedAddresses.province || "");
            setZipCode(savedAddresses.zipCode || "");
          }
        } else {
          setCardName(user?.name || "");
        }
      } catch (error) {
        console.error("Failed to parse auth_user from localStorage:", error);
      }
    }
  }, [orderMode.type]);

  const [isForceClosed, setIsForcedClosed] = useState(false);
  const [isTimeClosed, setIsTimeClosed] = useState(false);

  const [isAfterOfficeHours, setIsAfterOfficeHours] = useState(false);
  const [todayTiming, setTodayTiming] = useState<TimeSlot | null>(null);

  const toMinutes = (time: string) => {
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
  };

  const formatTime = (time: string) => {
    const [h, m] = time.split(":").map(Number);
    const hour12 = h % 12 || 12;
    const period = h >= 12 ? "PM" : "AM";
    return `${hour12}:${m.toString().padStart(2, "0")} ${period}`;
  };

  const SCHEDULE: Record<number, TimeSlot> = {
    0: { open: "10:45", close: "21:00" }, // Sunday
    1: { open: "10:45", close: "21:00" }, // Monday
    2: { open: "10:45", close: "21:00" }, // Tuesday
    3: { open: "10:45", close: "21:00" }, // Wednesday
    4: { open: "10:45", close: "21:00" }, // Thursday
    5: { open: "10:45", close: "21:00" }, // Friday
    6: { open: "10:45", close: "22:00" }, // Saturday
  };
  useEffect(() => {
    const checkTime = () => {
      const now = new Date();
      const today = now.getDay();
      const currentMinutes = now.getHours() * 60 + now.getMinutes();

      const schedule = SCHEDULE[today];
      if (!schedule) {
        setIsAfterOfficeHours(true);
        return;
      }

      const openMinutes = toMinutes(schedule.open);
      let closeMinutes = toMinutes(schedule.close);

      const isOvernight = closeMinutes < openMinutes;

      let isOpen: boolean;

      if (isOvernight) {
        // Example: 14:00 → 02:00
        isOpen = currentMinutes >= openMinutes || currentMinutes < closeMinutes;
      } else {
        isOpen = currentMinutes >= openMinutes && currentMinutes < closeMinutes;
      }

      setIsAfterOfficeHours(!isOpen);
      setTodayTiming(schedule);
    };

    checkTime();
    const interval = setInterval(checkTime, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setIsForcedClosed(restaurant?.isClosed === true);
    if (restaurant?.isClosed == false && isAfterOfficeHours && todayTiming) {
      setIsTimeClosed(true);
    } else {
      setIsTimeClosed(false);
    }
  }, [restaurant,isAfterOfficeHours, todayTiming]);

  useEffect(() => {
    if (orderMode.type == "pickup") {
      setDeliveryFee(0);
    } else {
      setDeliveryFee(apiConfig.DELIVERY_FEE);
    }
  }, [orderMode.type]);
  // Calculate tip
  // const tipAmount = customTip
  //   ? parseFloat(customTip) || 0
  //   : (subtotal * tipPercentage[0]) / 100;
  const total = subtotal + deliveryFee + tipAmount + tax + serviceFee;

  useEffect(() => {
    if (restaurant?.taxPercentage != null && !isNaN(subtotal)) {
      const calculatedTax = (subtotal * restaurant.taxPercentage) / 100;
      setTax(calculatedTax);
    } else {
      setTax(0);
    }
    if (restaurant?.serviceFee) {
      setServiceFee(restaurant.serviceFee);
    }
  }, [restaurant, subtotal]);

  // Get suggested items based on cart contents
  const getSuggestedItems = () => {
    if (cartItems.length === 0) return [];

    // Get categories from current cart items
    const cartCategories = [...new Set(cartItems.map((item) => item.category))];

    // Find items from same categories or popular items
    const suggestions = menuItems.filter((item: any) => {
      // Don't suggest items already in cart
      const isInCart = cartItems.some((cartItem) => cartItem.id === item.id);
      if (isInCart) return false;

      // Suggest items from same category or popular items
      return cartCategories.includes(item.category) || item.isFeatured;
    });

    // Return max 3 suggestions, prioritizing popular items
    return suggestions
      .sort((a: any, b: any) => (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0))
      .slice(0, 3);
  };

  const handleQuickAddSuggestion = (item: any) => {
    const defaultCustomizations = getDefaultCustomizations(
      item.mandatoryOptions,
    );
    const cartItem = {
      ...item,
      quantity: 1,
      totalPrice: item.price,
      customizations: defaultCustomizations,
    };
    addToCart(cartItem);
    setShowCartToast(true);
    setCartItemName(cartItem?.name);
    setCartItemQty(cartItem?.quantity);
  };
  const getDefaultCustomizations = (
    mandatoryOptions: OptionGroup[],
  ): Customization[] => {
    const customizations: Customization[] = [];

    mandatoryOptions.forEach((group) => {
      if (group.isRequired && group.options.length > 0) {
        const defaultOption = group.options[0]; // You can modify this to choose based on some other logic

        customizations.push({
          groupId: String(group.id),
          groupName: group.name,
          optionId: String(defaultOption.id),
          optionName: defaultOption.name,
          quantity: 1,
          price: defaultOption.price,
        });
      }
    });

    return customizations;
  };

  const mapCartItemsToDto = (cartItems: any[]): CartItemDto[] => {
    return cartItems.map((item) => ({
      id: item.id,
      name: item.name,
      basePrice: item.basePrice,
      discountPrice: item.discountPrice,
      quantity: item.quantity,
      totalPrice: item.totalPrice,
      specialInstructions: item.specialInstructions,
      customizations: item?.customizations?.map((c: any) => ({
        groupId: c.groupId,
        groupName: c.groupName,
        optionId: c.optionId,
        optionName: c.optionName,
        quantity: c.quantity,
        price: c.price,
      })),
    }));
  };

  // const handleSubmit = async (e: React.FormEvent) => {
  //   e.preventDefault();
  //   setIsProcessing(true);
  //   try {
  //     // 1. Call backend to create Stripe PaymentIntent (calculate total from cart)
  //     const clientSecretDTO: CreateClientSecret = {
  //       items: mapCartItemsToDto(cartItems),
  //       email: user?.email,
  //       phone: phone,
  //       address: address,
  //     };
  //     const intentData = await createPaymentIntent(clientSecretDTO);
  //     const clientSecret = intentData?.data?.clientSecret;

  //     if (!clientSecret)
  //       throw new Error("Failed to place order, please try again later");

  //     // 2. Confirm payment with Stripe
  //     if (!stripe || !elements) {
  //       throw new Error("Stripe has not loaded.");
  //     }
  //     const cardElement = elements.getElement(CardElement);

  //     if (!cardElement) {
  //       throw new Error("CardElement not found. Make sure it's rendered.");
  //     }

  //     const { error, paymentIntent } = await stripe.confirmCardPayment(
  //       clientSecret,
  //       {
  //         payment_method: {
  //           card: elements.getElement(CardElement)!,
  //           billing_details: {
  //             phone: phone,
  //             address: {
  //               line1: address,
  //             },
  //           },
  //         },
  //       }
  //     );

  //     if (error || paymentIntent?.status !== "succeeded") {
  //       throw new Error(error?.message || "Payment failed");
  //     }

  //     const orderDto: PlaceOrderDto = {
  //       customer: {
  //         name: cardName, // or any user input
  //         phone,
  //         email: user?.email,
  //       },
  //       deliveryAddress: address,
  //       specialInstructions: instructions,
  //       orderMode: orderMode.type,
  //       scheduledDate: orderMode.scheduledDate,
  //       scheduledTime: orderMode.scheduledTime,
  //       cartItems: mapCartItemsToDto(cartItems),
  //       paymentMethod: "card",
  //       tipAmount: tipAmount,
  //       deliveryFee,
  //       subtotal,
  //       total,
  //       stripeClientSecret: clientSecret,
  //       paymentIntentId: paymentIntent.id,
  //     };

  //     const orderResult = await placeOrder(orderDto);
  //     clearCart();
  //     navigate(`/order-confirmation/${orderResult.orderId}`);
  //   } catch (err: any) {
  //     console.error("Checkout error:", err);
  //     alert(err.message || "Checkout failed.");
  //   } finally {
  //     setIsProcessing(false);
  //   }
  // };
  const [paymentRequest, setPaymentRequest] = useState<any>(null);
  const [canMakePayment, setCanMakePayment] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);

  // Prepare PaymentRequest when payment method is Google Pay or Apple Pay
  useEffect(() => {
    if (!stripe) return;

    if (paymentMethod === "google-pay" || paymentMethod === "apple-pay") {
      // For simplicity, just use total as example amount here (convert to cents)
      const amountInCents = Math.round(total * 100);
      const rawCountry = restaurant?.branches?.[0]?.country;

      let stripeCountry: "US" | "CA" | null = null;
      let stripeCurrency: "usd" | "cad" | null = null;

      if (rawCountry === "United States") {
        stripeCountry = "US";
        stripeCurrency = "usd";
      } else if (rawCountry === "Canada") {
        stripeCountry = "CA";
        stripeCurrency = "cad";
      }
      if (!stripeCountry || !stripeCurrency) {
        console.warn("Unsupported or missing country:", rawCountry);
        setPaymentRequest(null);
        setCanMakePayment(false);
        setPaymentError("Payments are not available in this region.");
        return;
      }

      console.log("Amount in cents", amountInCents);
      // Create PaymentRequest object
      const pr = stripe.paymentRequest({
        country: stripeCountry,
        currency: stripeCurrency,
        total: {
          label: "Total",
          amount: amountInCents,
        },
        requestPayerName: true,
        requestPayerEmail: true,
        requestPayerPhone: true,
      });

      pr.canMakePayment().then((result) => {
        console.log("Result:", result);
        if (result) {
          console.log("Can make payment with:", result?.paymentRequestType);
          setCanMakePayment(true);
          setPaymentRequest(pr);
        } else {
          setCanMakePayment(false);
        }
      });

      pr.on("paymentmethod", async (ev) => {
        setIsProcessing(true);
        try {
          // 1. Create payment intent on backend
          const clientSecretDTO: CreateClientSecret = {
            items: mapCartItemsToDto(cartItems),
            userId: user?.id,
            email: user?.email,
            phone: phone,
            address: address,
            totalAmount: total,
          };

          const intentData = await createPaymentIntent(clientSecretDTO);
          const clientSecret = intentData?.data?.clientSecret;
          if (!clientSecret) throw new Error("Failed to create payment intent");

          // 2. Confirm payment using payment method from event
          const { error, paymentIntent } = await stripe.confirmCardPayment(
            clientSecret,
            { payment_method: ev.paymentMethod.id },
            { handleActions: false },
          );

          if (error) {
            ev.complete("fail");
            throw error;
          }

          ev.complete("success");

          if (paymentIntent.status === "requires_action") {
            const { error: actionError } =
              await stripe.confirmCardPayment(clientSecret);
            if (actionError) throw actionError;
          }

          // 3. Place order on backend with paymentIntent.id
          const orderDto: PlaceOrderDto = {
            customer: {
              id: user?.id || "",
              name: cardName, // or any user input
              phone,
              email: user?.email,
            },
            deliveryAddress: {
              fullname: cardName,
              addressLine1: address,
              city: city,
              state: state,
              zipCode: zipCode,
              deliveryInstructions: instructions,
              country: restaurant?.branches?.[0]?.country ?? "",
            },
            specialInstructions: specialInstructions,
            orderMode: orderMode.type,
            scheduledDate: orderMode.scheduledDate,
            scheduledTime: orderMode.scheduledTime,
            cartItems: mapCartItemsToDto(cartItems),
            paymentMethod: paymentMethod,
            tipAmount: tipAmount,
            taxAmount: tax,
            deliveryFee,
            serviceFee,
            subtotal,
            total,
            stripeClientSecret: clientSecret,
            paymentIntentId: paymentIntent.id,
            quoteId: quoteId,
          };

          const orderResult = await placeOrder(orderDto);
          clearCart();
          navigate(`/order-confirmation/${orderResult.orderId}`);
          // Optionally navigate or clear cart here
        } catch (err) {
          console.log(err || "Payment failed");
          ev.complete("fail");
        } finally {
          setIsProcessing(false);
        }
      });
    } else {
      setPaymentRequest(null);
      setCanMakePayment(false);
    }
  }, [stripe, paymentMethod]);

  // Called when clicking the "Place Order" button
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    console.log(
      "Selected order Type",
      orderMode.scheduledDate,
      orderMode.scheduledTime,
    );
    try {
      if (paymentMethod == "pay-later") {
        const orderDto: PlaceOrderDto = {
          customer: {
            id: user?.id || "",
            name: cardName, // or any user input
            phone,
            email: user?.email,
          },
          deliveryAddress: {
            fullname: cardName,
            addressLine1: address,
            city: city,
            state: state,
            zipCode: zipCode,
            country: restaurant?.branches?.[0]?.country ?? "",
            deliveryInstructions: instructions,
          },
          specialInstructions: specialInstructions,
          orderMode: orderMode.type,
          scheduledDate: orderMode.scheduledDate,
          scheduledTime: orderMode.scheduledTime,
          cartItems: mapCartItemsToDto(cartItems),
          paymentMethod: paymentMethod,
          tipAmount: tipAmount,
          taxAmount: tax,
          deliveryFee,
          serviceFee,
          subtotal,
          total,
          stripeClientSecret: "",
          paymentIntentId: "",
          quoteId: quoteId,
        };
        console.log(orderDto);
        const orderResult = await placeOrder(orderDto);
        clearCart();
        navigate(`/order-confirmation/${orderResult.orderId}`);
      } else {
        if (!stripe || !elements) throw new Error("Stripe has not loaded");

        // Create PaymentIntent once for all methods
        const clientSecretDTO: CreateClientSecret = {
          items: mapCartItemsToDto(cartItems),
          userId: user?.id || "",
          email: user?.email,
          phone: phone,
          address: address,
          totalAmount: total,
        };

        const intentData = await createPaymentIntent(clientSecretDTO);
        const clientSecret = intentData?.data?.clientSecret;
        if (!clientSecret) throw new Error("Failed to create payment intent");

        if (paymentMethod === "card") {
          const cardElement = elements.getElement(CardElement);
          if (!cardElement) throw new Error("Card element not found");

          const { error, paymentIntent } = await stripe.confirmCardPayment(
            clientSecret,
            {
              payment_method: {
                card: cardElement,
                billing_details: { phone, address: { line1: address } },
              },
            },
          );

          if (error) {
            console.error(error);
            alert(error.message || "Payment failed");
            return;
          }
          if (paymentIntent.status === "requires_action") {
            const { error: actionError, paymentIntent: finalIntent } =
              await stripe.confirmCardPayment(clientSecret);
            if (actionError) throw actionError;
            if (finalIntent.status !== "succeeded") {
              alert("Payment not successful, please try again.");
              return;
            }
          }

          const orderDto: PlaceOrderDto = {
            customer: {
              id: user?.id || "",
              name: cardName, // or any user input
              phone,
              email: user?.email,
            },
            deliveryAddress: {
              fullname: cardName,
              addressLine1: address,
              city: city,
              state: state,
              zipCode: zipCode,
              country: restaurant?.branches?.[0]?.country ?? "",
              deliveryInstructions: instructions,
            },
            specialInstructions: specialInstructions,
            orderMode: orderMode.type,
            scheduledDate: orderMode.scheduledDate,
            scheduledTime: orderMode.scheduledTime,
            cartItems: mapCartItemsToDto(cartItems),
            paymentMethod: "card",
            tipAmount: tipAmount,
            taxAmount: tax,
            deliveryFee,
            serviceFee,
            subtotal,
            total,
            stripeClientSecret: clientSecret,
            paymentIntentId: paymentIntent.id,
            quoteId: quoteId,
          };

          const orderResult = await placeOrder(orderDto);
          clearCart();
          navigate(`/order-confirmation/${orderResult.orderId}`);
        } else if (
          paymentMethod === "google-pay" ||
          paymentMethod === "apple-pay"
        ) {
          // For Google/Apple Pay, user must use PaymentRequestButton UI that triggers payment,
          // so you can show an alert or disable button here to avoid double payment.
          alert(
            `Please use the ${
              paymentMethod === "google-pay" ? "Google Pay" : "Apple Pay"
            } button to complete the payment.`,
          );
        }
      }
      // Clear cart or redirect here
    } catch (error) {
      console.log(error || "Payment failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleTipChange = (tip: string) => {
    if (tip === "custom") {
      setTipAmount(0);
      setCustomTip("");
    } else if (tip === "none") {
      setTipAmount(0);
      setCustomTip("");
    } else {
      const percentage = parseInt(tip) / 100;
      setTipAmount(subtotal * percentage);
      setCustomTip("");
    }
  };

  const handleCustomTipChange = (value: string) => {
    setCustomTip(value);
    const amount = parseFloat(value) || 0;
    setTipAmount(amount);
  };
  const getStepIcon = (step: CheckoutStep) => {
    switch (step) {
      case "delivery":
        return MapPin;
      case "pickup":
        return MapPin;
      case "payment":
        return CreditCard;
      case "review":
        return CheckCircle;
    }
  };

  const getStepTitle = (step: CheckoutStep) => {
    switch (step) {
      case "delivery":
        return "Delivery Info";
      case "pickup":
        return "Pickup Info";
      case "payment":
        return "Payment";
      case "review":
        return "Review Order";
    }
  };

  const isStepComplete = (step: CheckoutStep) => {
    switch (step) {
      case "delivery":
        return address && phone && city && state && zipCode;
      case "pickup":
        return phone;
      case "payment":
        return paymentMethod === "card" ? cardComplete : true;
      case "review":
        return true;
    }
  };

  const canProceedToNext = () => {
    return isStepComplete(currentStep);
  };
  const getStepError = () => {
    switch (currentStep) {
      case "delivery":
        if (!address) return "Please enter your address.";
        if (!city) return "Please enter your city.";
        if (!state) return "Please enter your state.";
        if (!zipCode) return "Please enter your ZIP code.";
        if (!phone) return "Please enter your phone number.";
        return null;

      case "pickup":
        if (!phone) return "Phone number is required.";
        return null;

      case "payment":
        if (paymentMethod === "card" && !cardComplete)
          return "Please complete card details.";
        return null;

      case "review":
        return null;

      default:
        return null;
    }
  };

  const handleNextClick = () => {
    const error = getStepError();

    if (error) {
      alert(error); // 👈 Simple alert popup
      return;
    }

    nextStep(); // proceed if no errors
  };
  const checkoutStepsList: CheckoutStep[] = useMemo(() => {
    const steps: CheckoutStep[] = ["delivery", "pickup", "payment", "review"];
    let filteredSteps = [];
    if (orderMode?.type === "pickup") {
      filteredSteps = steps.filter((step) => step !== "delivery");
    } else {
      filteredSteps = steps.filter((step) => step !== "pickup");
    }
    return filteredSteps;
    // return orderMode?.type === "pickup"
    //   ? steps.filter((step) => step !== "delivery")
    //   : steps;
  }, [orderMode]);

  const nextStep = async () => {
    try {
      console.log("Current Step", currentStep);
      if (currentStep == "delivery") {
        setIsProcessing(true);
        const orderDto: OrderQuotesDto = {
          customer: {
            id: user?.id || "",
            name: user?.name || "", // or any user input
            phone,
            email: user?.email,
          },
          deliveryAddress: {
            fullname: cardName,
            addressLine1: [address, city, state, zipCode]
              .filter(Boolean)
              .join(", "),
            city: city,
            state: state,
            zipCode: zipCode,
            country: restaurant?.branches?.[0]?.country ?? "",
            deliveryInstructions: instructions,
          },
          specialInstructions: specialInstructions,
          orderMode: orderMode.type,
          scheduledDate: orderMode.scheduledDate,
          scheduledTime: orderMode.scheduledTime,
          cartItems: mapCartItemsToDto(cartItems),
          paymentMethod: "card",
          tipAmount: tipAmount,
          taxAmount: tax,
          deliveryFee,
          serviceFee,
          subtotal,
          total,
        };
        const result = await requestQuotes(orderDto);
        setIsProcessing(false);
        console.log("res quote", result);
        if (
          !result ||
          result.is_available == null ||
          result?.is_available == false
        ) {
          console.log("res quote enet", result);

          setIsDeliveryServiceUnavailable(true);
          return;
        } else {
          setIsDeliveryServiceUnavailable(false);
          setQuoteId(result?.quote_id);
          if (result.fee == 0) {
            setDeliveryFee(apiConfig.DELIVERY_FEE);
          } else {
            setDeliveryFee(result?.fee);
          }
        }
      }
      const currentIndex = checkoutStepsList.indexOf(currentStep);
      if (currentIndex < checkoutStepsList.length - 1) {
        console.log("Next Step", checkoutStepsList[currentIndex + 1]);
        setCurrentStep(checkoutStepsList[currentIndex + 1]);
      }
    } catch (e) {
      setIsDeliveryServiceUnavailable(true);
      setIsProcessing(false);
    }
  };

  const prevStep = () => {
    const currentIndex = checkoutStepsList.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(checkoutStepsList[currentIndex - 1]);
    }
  };

  useEffect(() => {
    // if (orderMode.type === "pickup") {
    //   setCurrentStep("payment");
    // } else {
    //   setCurrentStep("delivery");
    // }
    setCurrentStep(orderMode.type);
  }, [orderMode]);

  const fetchMenu = async () => {
    try {
      const response = await getAllMenuItems();
      setMenuItems(response);
    } catch (err) {
      console.log(err);
    }
  };
  // Load items on screen mount
  useEffect(() => {
    fetchMenu();
  }, []);

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const onLoad = (autocomplete: google.maps.places.Autocomplete) => {
    autocompleteRef.current = autocomplete;
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current !== null) {
      const place = autocompleteRef.current.getPlace();

      if (!place.address_components) return;

      const addressComponents = place.address_components;

      const getComponent = (type: string) =>
        addressComponents.find((c) => c.types.includes(type))?.long_name || "";

      const streetNumber = getComponent("street_number");
      const route = getComponent("route");
      const city = getComponent("locality") || getComponent("sublocality");
      const state = getComponent("administrative_area_level_1");
      const zipCode = getComponent("postal_code");

      // Compose full address
      const fullAddress = `${streetNumber} ${route}`.trim();

      setAddress(place.formatted_address || "");
      setCity(city);
      setState(state);
      setZipCode(zipCode);
      const location = place.geometry?.location;
      if (location) {
        const lat = location.lat();
        const lng = location.lng();
        console.log("Coordinates:", lat, lng);
        // Save to state if needed
      }
    }
  };

  if (cartItems.length === 0) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center py-8 sm:py-12 mobile-padding animate-fade-in-up">
          <Card className="max-w-md w-full text-center animate-zoom-in">
            <CardContent className="p-6 sm:p-8">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce-gentle">
                <CreditCard className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                Your cart is empty
              </h2>
              <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
                Add some delicious items to your cart before checking out.
              </p>
              <Link to="/">
                <Button className="btn-primary w-full sm:w-auto">
                  Browse Menu
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </>
    );
  }
  const cardElementReady = elements?.getElement(CardElement) !== null;
  // Stack history maintaine
  useEffect(() => {
    const handleBack = () => {
      if (isDeliveryModalOpen) {
        setIsDeliveryModalOpen(false);
        return;
      }
    };

    window.addEventListener("popstate", handleBack);

    return () => {
      window.removeEventListener("popstate", handleBack);
    };
  }, [isDeliveryModalOpen]);

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-6 sm:py-8 animate-fade-in-up">
        <div className="max-w-6xl mx-auto mobile-padding pb-0">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 space-y-4 sm:space-y-0 animate-slide-in-down">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button
                  variant="ghost"
                  className="flex items-center space-x-2 transition-all duration-300 hover:-translate-x-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Back to Menu</span>
                  <span className="sm:hidden">Back</span>
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 font-display">
                  Checkout
                </h1>
                <p className="text-gray-600 text-sm sm:text-base">
                  Complete your order
                </p>
              </div>
            </div>
          </div>

          {/* Step Progress Indicator */}
          <div className="mb-8 animate-slide-in-down">
            <div className="flex items-center justify-between max-w-2xl mx-auto">
              {checkoutStepsList.map((step, index) => {
                const StepIcon = getStepIcon(step);
                const isActive = currentStep === step;
                const isCompleted =
                  checkoutStepsList.indexOf(currentStep) > index;

                return (
                  <div key={step} className="flex items-center">
                    <div
                      className={`flex flex-col items-center ${
                        index < 3 ? "flex-1" : ""
                      }`}
                    >
                      <div
                        className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                          isActive
                            ? "bg-emerald-600 text-white scale-110"
                            : isCompleted
                              ? "bg-emerald-500 text-white"
                              : "bg-gray-200 text-gray-500"
                        }`}
                      >
                        {isCompleted ? (
                          <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                        ) : (
                          <StepIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                        )}
                      </div>
                      <span
                        className={`text-xs sm:text-sm mt-2 font-medium ${
                          isActive
                            ? "text-emerald-600"
                            : isCompleted
                              ? "text-emerald-500"
                              : "text-gray-500"
                        }`}
                      >
                        {getStepTitle(step)}
                      </span>
                    </div>
                    {index < 3 && (
                      <div
                        className={`flex-1 h-0.5 mx-2 sm:mx-4 transition-all duration-300 ${
                          isCompleted ? "bg-emerald-500" : "bg-gray-200"
                        }`}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Main Content */}
            <div className="order-2 lg:order-1 lg:col-span-2 animate-slide-in-left space-y-6">
              {restaurant && (isForceClosed || isTimeClosed) && (
                <Card className="animate-zoom-in">
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2 text-red-700">
                      {/* Clock or alert icon */}
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="w-5 h-5 text-red-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6 0a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="font-semibold">Restaurant Closed</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm animate-fade-in">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mt-0.5 text-red-500 flex-shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01M5.07 19h13.86a2 2 0 001.74-3L13.74 4a2 2 0 00-3.48 0L3.33 16a2 2 0 001.74 3z"
                        />
                      </svg>
                      {/* FORCE CLOSED */}
                      {isForceClosed && (
                        <span>
                          {restaurant.closureReason
                            ? restaurant.closureReason
                            : "The restaurant is temporarily closed by the owner."}
                        </span>
                      )}

                      {/* OUTSIDE BUSINESS HOURS */}
                      {isTimeClosed && todayTiming && (
                        <span>
                          Orders can only be placed between{" "}
                          <strong>{formatTime(todayTiming.open)}</strong> and{" "}
                          <strong>{formatTime(todayTiming.close)}</strong>.
                          Please come back during our opening hours. Thank you!
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Order Type Selection */}
              {restaurant?.isDeliveryModeEnabled == true &&
                restaurant.isPickupModeEnabled == true && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        <Truck className="w-5 h-5 text-emerald-600" />
                        <span>Order Type</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <Button
                        onClick={() => {
                          setIsDeliveryModalOpen(true);
                          // Push new history state so back button closes modal
                          window.history.pushState({ modal: true }, "");
                        }}
                        variant="outline"
                        className="w-full justify-between h-14 bg-white hover:bg-gray-50 border-gray-200"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center">
                            {orderMode.type === "pickup" ? (
                              <Store className="w-5 h-5 text-white" />
                            ) : (
                              <Truck className="w-5 h-5 text-white" />
                            )}
                          </div>
                          <div className="text-left">
                            <div className="font-semibold text-gray-900">
                              {orderMode.type === "pickup"
                                ? "Pickup"
                                : "Delivery"}
                            </div>
                            <div className="text-sm text-gray-500">
                              {orderMode.type === "pickup"
                                ? "Ready in 15-20 min"
                                : "25-35 min delivery"}
                            </div>
                          </div>
                        </div>
                        <div className="text-sm text-emerald-600 font-medium">
                          Change
                        </div>
                      </Button>
                    </CardContent>
                  </Card>
                )}
              {currentStep == "delivery" ||
                (currentStep == "pickup" && (
                  <Card className="animate-zoom-in">
                    <CardHeader>
                      <CardTitle className="flex items-center space-x-2">
                        {(() => {
                          return (
                            <StickyNote className="w-5 h-5 text-emerald-600" />
                          );
                        })()}
                        <span>Order Info</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      {/* Special Instructions */}
                      <div className="space-y-3 p-3 sm:p-4 rounded-lg border border-gray-200 bg-white">
                        <h3 className="font-semibold text-gray-900 flex items-center space-x-2 text-sm sm:text-base">
                          <Label>Special Instructions</Label>
                          <Badge variant="secondary" className="text-xs">
                            Optional
                          </Badge>
                        </h3>
                        <Textarea
                          placeholder="Any special requests?"
                          value={specialInstructions}
                          onChange={(e) =>
                            setSpecialInstructions(e.target.value)
                          }
                          className="min-h-[60px] sm:min-h-[80px] border-gray-200 focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              <Card className="animate-zoom-in">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2 text-xl sm:text-2xl">
                    {(() => {
                      const StepIcon = getStepIcon(currentStep);
                      return <StepIcon className="w-6 h-6 text-emerald-600" />;
                    })()}
                    <span>{getStepTitle(currentStep)}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <LoadScript
                    googleMapsApiKey={googleMapsApiKey}
                    libraries={["places"]}
                  >
                    {/* Step 1: Delivery Information */}
                    {/* {currentStep === "delivery" && (
                    <div className="space-y-4 animate-slide-in-up">
                      <div className="space-y-2">
                        <Label htmlFor="address">Delivery Address</Label>
                        <Input
                          id="address"
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                          className="h-12 transition-all duration-300"
                          placeholder="Enter delivery address"
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="phone">Phone Number</Label>
                          <Input
                            id="phone"
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className="h-12 transition-all duration-300"
                            placeholder="(555) 123-4567"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Estimated Delivery</Label>
                          <div className="flex items-center space-x-2 h-12 px-3 border border-gray-200 rounded-lg bg-gray-50">
                            <Clock className="w-4 h-4 text-gray-500" />
                            <span className="text-gray-700">25-35 minutes</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="instructions">
                          Delivery Instructions (Optional)
                        </Label>
                        <Input
                          id="instructions"
                          value={instructions}
                          onChange={(e) => setInstructions(e.target.value)}
                          className="h-12 transition-all duration-300"
                          placeholder="e.g., Leave at door, Ring doorbell"
                        />
                      </div>
                    </div>
                  )} */}
                    {currentStep === "delivery" && (
                      <div className="space-y-4 animate-slide-in-up">
                        {/* Delivery unavailable message */}
                        {isDeliveryUnavailable && (
                          <div className="flex items-center gap-2 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm animate-fade-in">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 text-red-500"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01M5.07 19h13.86a2 2 0 001.74-3L13.74 4a2 2 0 00-3.48 0L3.33 16a2 2 0 001.74 3z"
                              />
                            </svg>
                            <span>
                              Sorry, delivery service is unavailable at this
                              address.
                            </span>
                          </div>
                        )}
                        <div className="space-y-2">
                          <Label htmlFor="address">Delivery Address</Label>
                          <Autocomplete
                            onLoad={onLoad}
                            onPlaceChanged={onPlaceChanged}
                          >
                            <Input
                              id="address"
                              value={address}
                              onChange={(e) => setAddress(e.target.value)}
                              className="h-12 transition-all duration-300"
                              placeholder="Enter delivery address"
                            />
                          </Autocomplete>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="phone">Phone Number</Label>
                            <Input
                              id="phone"
                              type="tel"
                              value={phone}
                              onChange={(e) => setPhone(e.target.value)}
                              className="h-12 transition-all duration-300"
                              placeholder="Enter Phone Number"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Estimated Delivery</Label>
                            <div className="flex items-center space-x-2 h-12 px-3 border border-gray-200 rounded-lg bg-gray-50">
                              <Clock className="w-4 h-4 text-gray-500" />
                              <span className="text-gray-700">
                                25-35 minutes
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="city">City</Label>
                            <Input
                              id="city"
                              type="text"
                              value={city}
                              onChange={(e) => setCity(e.target.value)}
                              className="h-12 transition-all duration-300"
                              placeholder="City"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="state">Province</Label>
                            <Input
                              id="state"
                              type="text"
                              value={state}
                              onChange={(e) => setState(e.target.value)}
                              className="h-12 transition-all duration-300"
                              placeholder="Province"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="zipCode">Zip Code</Label>
                          <Input
                            id="zipCode"
                            type="text"
                            value={zipCode}
                            onChange={(e) => setZipCode(e.target.value)}
                            className="h-12 transition-all duration-300"
                            placeholder="Zip Code"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="instructions">
                            Delivery Instructions (Optional)
                          </Label>
                          <Input
                            id="instructions"
                            value={instructions}
                            onChange={(e) => setInstructions(e.target.value)}
                            className="h-12 transition-all duration-300"
                            placeholder="e.g., Leave at door, Ring doorbell"
                          />
                        </div>
                      </div>
                    )}
                    {currentStep === "pickup" && (
                      <div className="space-y-4 animate-slide-in-up">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="phone">Full Name</Label>
                            <Input
                              id="name"
                              type="text"
                              value={cardName}
                              onChange={(e) => setCardName(e.target.value)}
                              className="h-12 transition-all duration-300"
                              placeholder="Enter Full Name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="phone">Phone Number</Label>
                            <Input
                              id="phone"
                              type="tel"
                              value={phone}
                              onChange={(e) => setPhone(e.target.value)}
                              className="h-12 transition-all duration-300"
                              placeholder="Enter Phone Number"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Estimated Pickup Time</Label>
                            <div className="flex items-center space-x-2 h-12 px-3 border border-gray-200 rounded-lg bg-gray-50">
                              <Clock className="w-4 h-4 text-gray-500" />
                              <span className="text-gray-700">
                                25-35 minutes
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 3: Payment */}
                    {/* {currentStep === "payment" && ( */}
                    <div
                      // className="space-y-6 animate-slide-in-up"
                      className={`space-y-6 animate-slide-in-up transition-all duration-300 ${
                        currentStep === "payment" ? "block" : "hidden"
                      }`}
                    >
                      {/* Payment Method Selection */}
                      <RadioGroup
                        value={paymentMethod}
                        onValueChange={(c) => {
                          console.log("Old Payment Method", paymentMethod);
                          setPaymentMethod(c);
                          console.log("Selected Payment Method", c);
                        }}
                      >
                        <div className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:border-emerald-300 transition-all duration-300 hover:scale-105">
                          <RadioGroupItem value="card" id="card" />
                          <Label
                            htmlFor="card"
                            className="flex items-center space-x-3 cursor-pointer flex-1"
                          >
                            <CreditCard className="w-5 h-5 text-gray-600" />
                            <div>
                              <div className="font-medium text-gray-900">
                                Credit or Debit Card
                              </div>
                              <div className="text-sm text-gray-500">
                                Visa, Mastercard, American Express, Discover
                              </div>
                            </div>
                          </Label>
                        </div>

                        <div className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:border-emerald-300 transition-all duration-300 hover:scale-105">
                          <RadioGroupItem value="google-pay" id="google-pay" />
                          <Label
                            htmlFor="google-pay"
                            className="flex items-center space-x-3 cursor-pointer flex-1"
                          >
                            <Wallet className="w-5 h-5 text-gray-600" />
                            <div>
                              <div className="font-medium text-gray-900">
                                Google Pay
                              </div>
                              <div className="text-sm text-gray-500">
                                Pay securely with your Google account
                              </div>
                            </div>
                          </Label>
                        </div>

                        <div className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:border-emerald-300 transition-all duration-300 hover:scale-105">
                          <RadioGroupItem value="apple-pay" id="apple-pay" />
                          <Label
                            htmlFor="apple-pay"
                            className="flex items-center space-x-3 cursor-pointer flex-1"
                          >
                            <Smartphone className="w-5 h-5 text-gray-600" />
                            <div>
                              <div className="font-medium text-gray-900">
                                Apple Pay
                              </div>
                              <div className="text-sm text-gray-500">
                                Pay with Touch ID, Face ID, or Apple Watch
                              </div>
                            </div>
                          </Label>
                        </div>
                        {orderMode?.type === "pickup" &&
                          restaurant?.payLaterEnabled == true && (
                            <div className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:border-emerald-300 transition-all duration-300 hover:scale-105">
                              <RadioGroupItem
                                value="pay-later"
                                id="pay-later"
                              />
                              <Label
                                htmlFor="pay-later"
                                className="flex items-center space-x-3 cursor-pointer flex-1"
                              >
                                <Clock className="w-5 h-5 text-gray-600" />
                                <div>
                                  <div className="font-medium text-gray-900">
                                    Pay Later
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    Pay when you pick up or receive delivery
                                  </div>
                                </div>
                              </Label>
                            </div>
                          )}
                      </RadioGroup>
                      {/* {paymentMethod === "card" && ( */}
                      <div
                        // className="space-y-4 animate-slide-in-up"
                        className={`transition-all duration-300 ${
                          currentStep === "payment" && paymentMethod === "card"
                            ? "block"
                            : "hidden"
                        }`}
                      >
                        {/* <div className="space-y-2">
                          <Label htmlFor="card-name">Cardholder Name</Label>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <Input
                              id="card-name"
                              type="text"
                              placeholder="John Doe"
                              value={cardName}
                              onChange={(e) => setCardName(e.target.value)}
                              className="pl-10 h-12 transition-all duration-300"
                              required
                            />
                          </div>
                        </div> */}

                        <div className="space-y-2">
                          <Label>Card Details</Label>
                          <div className="relative border border-gray-300 rounded-md px-3 py-2">
                            <CardElement
                              id="card-element"
                              options={{
                                style: {
                                  base: {
                                    fontSize: "16px",
                                    color: "#1a202c",
                                    "::placeholder": {
                                      color: "#a0aec0",
                                    },
                                  },
                                  invalid: {
                                    color: "#e53e3e",
                                  },
                                },
                              }}
                              onChange={(event) => {
                                setCardComplete(event.complete); // true when full card info is filled
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      {/* )} */}

                      {/* Google Pay */}
                      {/* {paymentMethod === "google-pay" && ( */}
                      <div
                        className={`transition-all duration-300 ${
                          currentStep === "payment" &&
                          paymentMethod === "google-pay"
                            ? "block"
                            : "hidden"
                        }`}
                      >
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-lg text-center animate-slide-in-up">
                          <Wallet className="w-12 h-12 mx-auto mb-4 animate-bounce-gentle" />
                          <h3 className="text-lg font-semibold mb-2">
                            Pay with Google Pay
                          </h3>
                          <p className="text-blue-100 text-sm mb-4">
                            {canMakePayment
                              ? "Complete your payment securely using your saved Google payment methods"
                              : "Google Pay is not available on your device or browser. Please choose another payment method."}
                          </p>

                          {/* <div className="flex justify-center mt-4">
                          {paymentMethod === "google-pay" &&
                            canMakePayment &&
                            paymentRequest && (
                              <PaymentRequestButtonElement
                                options={{ paymentRequest }}
                              />
                            )}
                        </div> */}
                        </div>
                      </div>
                      {/* )} */}

                      {/* Apple Pay */}
                      {/* {paymentMethod === "apple-pay" && ( */}
                      <div
                        className={`transition-all duration-300 ${
                          currentStep === "payment" &&
                          paymentMethod === "apple-pay"
                            ? "block"
                            : "hidden"
                        }`}
                      >
                        <div className="bg-black text-white p-6 rounded-lg text-center animate-slide-in-up">
                          <Smartphone className="w-12 h-12 mx-auto mb-4 animate-bounce-gentle" />
                          <h3 className="text-lg font-semibold mb-2">
                            Pay with Apple Pay
                          </h3>
                          <p className="text-blue-100 text-sm mb-4">
                            {canMakePayment
                              ? "Use Apple Pay with Touch ID, Face ID, or Apple Watch to complete your payment securely."
                              : "Apple Pay is not available on your device or browser. Please choose another payment method."}
                          </p>

                          {/* {paymentMethod === "apple-pay" &&
                          canMakePayment &&
                          paymentRequest && (
                            <PaymentRequestButtonElement
                              options={{ paymentRequest }}
                            />
                          )} */}
                        </div>
                      </div>
                      {/* Pay later */}
                      <div
                        className={`transition-all duration-300 ${
                          currentStep === "payment" &&
                          paymentMethod === "pay-later"
                            ? "block"
                            : "hidden"
                        }`}
                      >
                        <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg text-center animate-slide-in-up">
                          <Clock className="w-12 h-12 mx-auto mb-4 text-yellow-600" />
                          <h3 className="text-lg font-semibold mb-2 text-yellow-800">
                            Pay When You Receive
                          </h3>
                          <p className="text-yellow-700 text-sm mb-4">
                            You can pay with cash or card when you pick it up
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Step 4: Review Order */}
                    {currentStep === "review" && (
                      <div className="space-y-6 animate-slide-in-up">
                        <div className="text-center">
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">
                            Review Your Order
                          </h3>
                          <p className="text-gray-600">
                            Please review your order details before placing
                          </p>
                        </div>

                        {/* Order Summary */}
                        {orderMode.type == "delivery" && (
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-semibold mb-3">
                              Delivery Information
                            </h4>
                            <div className="space-y-2 text-sm">
                              <div className="flex items-center space-x-2">
                                <MapPin className="w-4 h-4 text-gray-500" />
                                <span>{address}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <User className="w-4 h-4 text-gray-500" />
                                <span>{phone}</span>
                              </div>
                              {instructions && (
                                <div className="flex items-start space-x-2">
                                  <Package className="w-4 h-4 text-gray-500 mt-0.5" />
                                  <span>{instructions}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {orderMode.type == "pickup" && (
                          <div className="bg-gray-50 p-4 rounded-lg">
                            <h4 className="font-semibold mb-3">
                              Pickup Information
                            </h4>
                            {cardName && (
                              <div className="space-y-2 text-sm">
                                <div className="flex items-center space-x-2">
                                  <User className="w-4 h-4 text-gray-500" />
                                  <span>{cardName}</span>
                                </div>
                              </div>
                            )}
                            <div className="space-y-2 text-sm">
                              <div className="flex items-center space-x-2">
                                <Phone className="w-4 h-4 text-gray-500" />
                                <span>{formatCanadianPhoneNumber(phone)}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="bg-gray-50 p-4 rounded-lg">
                          <h4 className="font-semibold mb-3">Payment Method</h4>
                          <div className="flex items-center space-x-3">
                            {paymentMethod === "card" && (
                              <div className="flex items-center space-x-3 text-sm text-gray-600">
                                <CreditCard className="w-5 h-5 text-gray-500" />
                                {/* <span>{cardName}</span> */}
                                <span className="text-gray-400 italic">
                                  Card details entered securely
                                </span>
                              </div>
                            )}

                            {paymentMethod === "google-pay" && (
                              <Wallet className="w-5 h-5 text-gray-600" />
                            )}
                            {paymentMethod === "apple-pay" && (
                              <Smartphone className="w-5 h-5 text-gray-600" />
                            )}
                            <span className="capitalize">
                              {paymentMethod.replace("-", " ")}
                            </span>
                            {paymentMethod === "card" && cardNumber && (
                              <span className="text-gray-500">
                                •••• {cardNumber.slice(-4)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Navigation Buttons */}
                    <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t">
                      {currentStep !== checkoutStepsList[0] && (
                        <Button
                          variant="outline"
                          onClick={prevStep}
                          className="flex-1 h-12 transition-all duration-300 hover:scale-105"
                        >
                          <ArrowLeft className="w-4 h-4 mr-2" />
                          Previous
                        </Button>
                      )}

                      {currentStep !== "review" ? (
                        <Button
                          // onClick={nextStep}
                          // disabled={!canProceedToNext() || isProcessing}
                          onClick={handleNextClick}
                          disabled={
                            !stripe ||
                            !elements ||
                            !cardElementReady ||
                            isProcessing ||
                            isForceClosed ||
                            isTimeClosed
                          }
                          className="flex-1 h-12 btn-primary transition-all duration-300 hover:scale-105"
                        >
                          {isProcessing ? (
                            <div className="flex items-center space-x-2">
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                              <span>Processing...</span>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span>Next Step</span>
                              <ArrowRight className="w-4 h-4 ml-2" />
                            </div>
                          )}
                        </Button>
                      ) : (
                        <div className="flex-1 space-y-3">
                          {paymentMethod === "card" && (
                            <Button
                              onClick={handleSubmit}
                              className="w-full h-12 btn-primary text-lg font-bold"
                              disabled={
                                !stripe ||
                                !elements ||
                                !cardElementReady ||
                                isProcessing
                              }
                            >
                              {isProcessing ? (
                                <div className="flex items-center space-x-2">
                                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  <span>Processing...</span>
                                </div>
                              ) : (
                                <div className="flex items-center space-x-2">
                                  <CreditCard className="w-5 h-5" />
                                  <span>
                                    Place Order - ${total?.toFixed(2)}
                                  </span>
                                </div>
                              )}
                            </Button>
                          )}

                          {paymentMethod === "google-pay" && (
                            // <Button
                            //   onClick={handleGooglePay}
                            //   className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12 font-semibold transition-all duration-300 hover:scale-105"
                            //   disabled={isProcessing}
                            // >
                            //   {isProcessing ? (
                            //     <div className="flex items-center space-x-2">
                            //       <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            //       <span>Processing...</span>
                            //     </div>
                            //   ) : (
                            //     `Pay $${total?.toFixed(2)} with Google Pay`
                            //   )}
                            // </Button>
                            <div className="flex flex-col items-center space-y-4">
                              <p className="text-sm text-gray-200">
                                Pay securely with Google Pay
                              </p>
                              <div className="w-full max-w-md">
                                <PaymentRequestButtonElement
                                  options={{
                                    paymentRequest,
                                    style: {
                                      paymentRequestButton: {
                                        type: "buy",
                                        theme: "dark",
                                        height: "48px",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </div>
                          )}

                          {paymentMethod === "apple-pay" && (
                            // <Button
                            //   onClick={handleApplePay}
                            //   className="w-full bg-black hover:bg-gray-800 text-white h-12 font-semibold transition-all duration-300 hover:scale-105"
                            //   disabled={isProcessing}
                            // >
                            //   {isProcessing ? (
                            //     <div className="flex items-center space-x-2">
                            //       <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            //       <span>Processing...</span>
                            //     </div>
                            //   ) : (
                            //     `Pay $${total?.toFixed(2)} with Apple Pay`
                            //   )}
                            // </Button>
                            <div className="flex flex-col items-center space-y-4">
                              <p className="text-sm text-gray-200">
                                Pay securely with Apple Pay
                              </p>
                              <div className="w-full max-w-md">
                                <PaymentRequestButtonElement
                                  options={{
                                    paymentRequest,
                                    style: {
                                      paymentRequestButton: {
                                        type: "buy",
                                        theme: "dark",
                                        height: "48px",
                                      },
                                    },
                                  }}
                                />
                              </div>
                            </div>
                          )}
                          {paymentMethod === "pay-later" && (
                            <Button
                              onClick={handleSubmit}
                              className="w-full h-12 btn-primary text-lg font-bold"
                              disabled={isProcessing}
                            >
                              {isProcessing ? (
                                <div className="flex items-center space-x-2">
                                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                  <span>Processing...</span>
                                </div>
                              ) : (
                                <div className="flex items-center space-x-2">
                                  <CreditCard className="w-5 h-5" />
                                  <span>
                                    Place Order - ${total?.toFixed(2)}
                                  </span>
                                </div>
                              )}
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  </LoadScript>
                </CardContent>
              </Card>
            </div>

            {/* Order Summary Sidebar */}
            <div className="order-1 lg:order-2 lg:col-span-1 animate-slide-in-right">
              <Card className="sticky top-28">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <span>Order Summary</span>
                    <Badge className="text-xs">
                      {cartItems.reduce((sum, item) => sum + item.quantity, 0)}{" "}
                      items
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Cart Items */}
                  <div className="space-y-3 max-h-64 overflow-y-auto custom-scrollbar">
                    {cartItems.map((item, index) => (
                      <div
                        key={`${item.id}-${index}`}
                        className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <img
                          src={getMenuItemImageUrl(item.imageUrl)}
                          alt={item.name}
                          className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-sm text-gray-900 line-clamp-1">
                            {item.name}
                          </h4>
                          <p className="text-sm text-emerald-600 font-bold">
                            $
                            {(
                              (item.totalPrice || item.basePrice) *
                              item.quantity
                            )?.toFixed(2)}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <div className="flex items-center space-x-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  updateCartQuantity(item.id, item.quantity - 1)
                                }
                                disabled={item.quantity <= 1}
                                className="h-6 w-6 p-0"
                              >
                                <Minus className="w-3 h-3" />
                              </Button>
                              <span className="w-6 text-center text-sm font-bold">
                                {item.quantity}
                              </span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  updateCartQuantity(item.id, item.quantity + 1)
                                }
                                className="h-6 w-6 p-0"
                              >
                                <Plus className="w-3 h-3" />
                              </Button>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFromCart(item.id)}
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Separator />
                  {/* Tip Section */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <DollarSign className="w-4 h-4 text-emerald-600" />
                      <h4 className="font-semibold text-gray-900 text-sm">
                        Add Tip
                      </h4>
                      <Badge variant="secondary" className="text-xs">
                        Optional
                      </Badge>
                    </div>

                    <div className="grid grid-cols-4 gap-2">
                      {["15", "18", "20", "25"].map((percentage) => (
                        <Button
                          key={percentage}
                          variant={
                            tipAmount ===
                            (subtotal * parseInt(percentage)) / 100
                              ? "default"
                              : "outline"
                          }
                          size="sm"
                          onClick={() => handleTipChange(percentage)}
                          className="h-10 text-xs"
                        >
                          {percentage}%
                        </Button>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant={tipAmount === 0 ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleTipChange("none")}
                        className="h-10 text-xs"
                      >
                        No Tip
                      </Button>
                      <div className="relative">
                        <input
                          type="number"
                          placeholder="Custom $"
                          value={customTip}
                          onChange={(e) =>
                            handleCustomTipChange(e.target.value)
                          }
                          className="w-full h-10 px-3 text-xs border border-gray-200 rounded-md focus:border-emerald-500 focus:ring-emerald-500"
                          min="0"
                          step="0.01"
                        />
                      </div>
                    </div>

                    {tipAmount > 0 && (
                      <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-emerald-800 font-medium">
                            Tip Amount:
                          </span>
                          <span className="text-emerald-800 font-bold">
                            ${tipAmount.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  <Separator />
                  {/* Order Totals */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal</span>
                      <span>${subtotal?.toFixed(2)}</span>
                    </div>
                    {/* {orderMode?.type === "pickup" ? (
                      <div className="flex justify-between text-sm">
                        <span className="text-green-600">
                          Pickup - No Delivery Fee
                        </span>
                        <span className="font-semibold text-green-600">0</span>
                      </div>
                    ) : (
                      <div className="flex justify-between text-sm">
                        <span>Delivery Fee</span>
                        <span>${deliveryFee?.toFixed(2)}</span>
                      </div>
                    )} */}
                    {orderMode?.type === "delivery" && (
                      <div className="flex justify-between text-sm">
                        <span>Delivery Fee</span>
                        {deliveryFee ? (
                          <span>${deliveryFee.toFixed(2)}</span>
                        ) : (
                          <span className="text-[12px] text-gray-400 italic text-right w-full block">
                            Delivery charges will be additional
                          </span>
                        )}
                      </div>
                    )}

                    <div className="flex justify-between text-sm items-center">
                      <div className="flex items-center gap-1 relative">
                        <span>Taxes and Service fee</span>
                        <button
                          type="button"
                          onClick={() => setOpenTaxToolTip(!openTaxToolTip)}
                          onMouseEnter={() => setOpenTaxToolTip(true)}
                          onMouseLeave={() => setOpenTaxToolTip(false)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          <Info size={18} />
                        </button>
                        {/* Tooltip */}
                        {openTaxToolTip && (
                          <div className="absolute top-6 left-0 z-50 w-52 rounded-md border bg-white p-3 shadow-lg text-xs">
                            {restaurant?.taxRules?.map((tax) => (
                              <div
                                key={tax.name}
                                className="flex justify-between"
                              >
                                <span>
                                  {tax.name} ({tax.percentage}%)
                                </span>
                                <span>
                                  $
                                  {((subtotal * tax.percentage) / 100).toFixed(
                                    2,
                                  )}
                                </span>
                              </div>
                            ))}
                            <div className="flex justify-between">
                              <span>Service fee</span>
                              <span>${restaurant?.serviceFee?.toFixed(2)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                      <span>${(tax + serviceFee)?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Tip</span>
                      <span>${tipAmount?.toFixed(2)}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total</span>
                      <span className="text-emerald-600">
                        ${total?.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Security Notice */}
                  <div className="bg-emerald-50 p-3 rounded-lg border border-emerald-200">
                    <div className="flex items-center space-x-2 text-emerald-800 text-sm">
                      <Lock className="w-4 h-4" />
                      <span>
                        Your payment information is secure and encrypted
                      </span>
                    </div>
                  </div>

                  {/* Suggested Items */}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Complete Your Meal Section - Right after checkout */}
        {cartItems.length > 0 && (
          <div className="max-w-6xl mx-auto mobile-padding mt-6">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 font-display">
                    Complete Your Meal
                  </h3>
                  <p className="text-gray-600 text-sm">
                    Popular items that go great with your order
                  </p>
                </div>
                <Badge className="bg-orange-100 text-orange-800 text-xs">
                  Recommended
                </Badge>
              </div>

              {/* Horizontal Slider */}
              <div className="relative">
                <div className="flex space-x-4 overflow-x-auto pb-4 scrollbar-hide snap-x snap-mandatory">
                  {getSuggestedItems().map((item: any) => (
                    <div
                      key={item.id}
                      className="flex-shrink-0 w-64 bg-gradient-to-br from-gray-50 to-white rounded-xl border border-gray-200 hover:border-orange-300 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 p-4 snap-start"
                    >
                      <div className="flex items-start space-x-3">
                        <img
                          src={getMenuItemImageUrl(item.imageUrl)}
                          alt={item.name}
                          className="w-16 h-16 object-cover rounded-lg shadow-sm flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-gray-900 text-sm line-clamp-1 mb-1">
                            {item.name}
                          </h4>
                          <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                            {item.description}
                          </p>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-bold text-emerald-600">
                                ${item.basePrice?.toFixed(2)}
                              </span>
                              {item.isFeatured && (
                                <Badge className="bg-orange-100 text-orange-800 text-xs px-1.5 py-0.5">
                                  <Star className="w-2.5 h-2.5 mr-0.5 fill-current" />
                                  Hot
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Button
                            onClick={() => handleQuickAddSuggestion(item)}
                            size="sm"
                            className="w-full mt-3 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white h-8 text-xs font-semibold rounded-lg transition-all duration-200 hover:scale-105"
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Quick Add
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Add more variety items for better scrolling */}
                  {getSuggestedItems().length < 6 &&
                    menuItems
                      .filter(
                        (item: any) =>
                          !cartItems.some(
                            (cartItem) => cartItem.id === item.id,
                          ) &&
                          !getSuggestedItems().some(
                            (suggested: any) => suggested.id === item.id,
                          ),
                      )
                      .slice(0, 4)
                      .map((item: any) => (
                        <div
                          key={`extra-${item.id}`}
                          className="flex-shrink-0 w-64 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 p-4 snap-start"
                        >
                          <div className="flex items-start space-x-3">
                            <img
                              src={getMenuItemImageUrl(item.imageUrl)}
                              alt={item.name}
                              className="w-16 h-16 object-cover rounded-lg shadow-sm flex-shrink-0"
                            />
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-gray-900 text-sm line-clamp-1 mb-1">
                                {item.name}
                              </h4>
                              <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                                {item.description}
                              </p>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-bold text-emerald-600">
                                    ${item.basePrice?.toFixed(2)}
                                  </span>
                                  <Badge className="bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5">
                                    Try This
                                  </Badge>
                                </div>
                              </div>
                              <Button
                                onClick={() => handleQuickAddSuggestion(item)}
                                size="sm"
                                className="w-full mt-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white h-8 text-xs font-semibold rounded-lg transition-all duration-200 hover:scale-105"
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Add to Cart
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                </div>

                {/* Scroll Indicator */}
                <div className="flex justify-center mt-3">
                  <div className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    ← Swipe or scroll to see more delicious options →
                  </div>
                </div>
              </div>

              {/* Bottom Message */}
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-600">
                  💡 <strong>Pro Tip:</strong> These items are frequently
                  ordered together and complement your meal perfectly
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      {/* Delivery Type Modal */}
      <DeliveryTypeModal
        isOpen={isDeliveryModalOpen}
        onClose={() => {
          setIsDeliveryModalOpen(false);
          // Remove the pushed state
          if (window.history.state?.modal) {
            window.history.back();
          }
        }}
        orderMode={orderMode}
        onOrderModeChange={setOrderMode}
      />
      {/* Item added notification */}
      <CartNotification
        isVisible={showCartToast}
        itemName={cartItemName}
        quantity={cartItemQty}
        onClose={() => setShowCartToast(false)}
      />
      <Footer />
    </>
  );
}
